
import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';

interface Question {
  id: number;
  category: string;
  question: string;
  options: { text: string; score: number }[];
  icon: string;
}

const questions: Question[] = [
  {
    id: 1,
    category: 'Heart Health',
    question: 'How often do you engage in cardiovascular exercise (walking, running, swimming)?',
    icon: 'ri-heart-pulse-line',
    options: [
      { text: '5+ times per week', score: 4 },
      { text: '3-4 times per week', score: 3 },
      { text: '1-2 times per week', score: 2 },
      { text: 'Rarely or never', score: 1 },
    ],
  },
  {
    id: 2,
    category: 'Heart Health',
    question: 'Do you know your current blood pressure numbers?',
    icon: 'ri-heart-pulse-line',
    options: [
      { text: 'Yes, and it\'s in the healthy range', score: 4 },
      { text: 'Yes, but it\'s slightly elevated', score: 3 },
      { text: 'I had it checked recently but don\'t remember', score: 2 },
      { text: 'I haven\'t checked in over a year', score: 1 },
    ],
  },
  {
    id: 3,
    category: 'Nutrition',
    question: 'How many servings of fruits and vegetables do you eat daily?',
    icon: 'ri-leaf-line',
    options: [
      { text: '5 or more servings', score: 4 },
      { text: '3-4 servings', score: 3 },
      { text: '1-2 servings', score: 2 },
      { text: 'Less than 1 serving', score: 1 },
    ],
  },
  {
    id: 4,
    category: 'Nutrition',
    question: 'How often do you consume processed or fast food?',
    icon: 'ri-leaf-line',
    options: [
      { text: 'Rarely (once a month or less)', score: 4 },
      { text: 'Occasionally (1-2 times per week)', score: 3 },
      { text: 'Frequently (3-4 times per week)', score: 2 },
      { text: 'Daily', score: 1 },
    ],
  },
  {
    id: 5,
    category: 'Mental Wellness',
    question: 'How would you rate your stress levels on most days?',
    icon: 'ri-mental-health-line',
    options: [
      { text: 'Low - I feel calm and balanced', score: 4 },
      { text: 'Moderate - Some stress but manageable', score: 3 },
      { text: 'High - Often feel overwhelmed', score: 2 },
      { text: 'Very high - Constantly stressed', score: 1 },
    ],
  },
  {
    id: 6,
    category: 'Mental Wellness',
    question: 'How often do you practice self-care or relaxation activities?',
    icon: 'ri-mental-health-line',
    options: [
      { text: 'Daily', score: 4 },
      { text: 'A few times per week', score: 3 },
      { text: 'Occasionally', score: 2 },
      { text: 'Rarely or never', score: 1 },
    ],
  },
  {
    id: 7,
    category: 'Sleep',
    question: 'How many hours of sleep do you typically get per night?',
    icon: 'ri-moon-line',
    options: [
      { text: '7-9 hours', score: 4 },
      { text: '6-7 hours', score: 3 },
      { text: '5-6 hours', score: 2 },
      { text: 'Less than 5 hours', score: 1 },
    ],
  },
  {
    id: 8,
    category: 'Sleep',
    question: 'How would you rate the quality of your sleep?',
    icon: 'ri-moon-line',
    options: [
      { text: 'Excellent - I wake up refreshed', score: 4 },
      { text: 'Good - Generally restful', score: 3 },
      { text: 'Fair - Sometimes restless', score: 2 },
      { text: 'Poor - Frequently disrupted', score: 1 },
    ],
  },
  {
    id: 9,
    category: 'Preventive Care',
    question: 'When was your last comprehensive health checkup?',
    icon: 'ri-stethoscope-line',
    options: [
      { text: 'Within the last year', score: 4 },
      { text: '1-2 years ago', score: 3 },
      { text: '2-3 years ago', score: 2 },
      { text: 'More than 3 years ago', score: 1 },
    ],
  },
  {
    id: 10,
    category: 'Preventive Care',
    question: 'Are you up to date on recommended health screenings for your age?',
    icon: 'ri-stethoscope-line',
    options: [
      { text: 'Yes, all screenings are current', score: 4 },
      { text: 'Most are current', score: 3 },
      { text: 'Some are overdue', score: 2 },
      { text: 'I\'m not sure what screenings I need', score: 1 },
    ],
  },
];

const getResultCategory = (score: number) => {
  const percentage = (score / 40) * 100;
  if (percentage >= 80) {
    return {
      title: 'Excellent Health Habits!',
      color: 'green',
      icon: 'ri-trophy-line',
      message: 'You\'re doing a fantastic job taking care of your health! Keep up the great work and continue prioritizing your wellness journey.',
      recommendations: [
        'Continue your current healthy habits',
        'Consider mentoring others on their wellness journey',
        'Stay consistent with preventive screenings',
        'Explore advanced wellness practices like meditation retreats',
      ],
    };
  } else if (percentage >= 60) {
    return {
      title: 'Good Foundation',
      color: 'teal',
      icon: 'ri-thumb-up-line',
      message: 'You have a solid foundation for good health! There are some areas where small improvements could make a big difference.',
      recommendations: [
        'Focus on improving 1-2 areas at a time',
        'Schedule any overdue health screenings',
        'Consider adding more physical activity',
        'Explore our wellness programs for additional support',
      ],
    };
  } else if (percentage >= 40) {
    return {
      title: 'Room for Improvement',
      color: 'amber',
      icon: 'ri-lightbulb-line',
      message: 'Your health journey has room for growth. The good news is that small, consistent changes can lead to significant improvements!',
      recommendations: [
        'Start with one healthy habit change per week',
        'Schedule a comprehensive health checkup',
        'Join our Heart Health Education program',
        'Connect with our support groups for motivation',
      ],
    };
  } else {
    return {
      title: 'Time to Take Action',
      color: 'coral',
      icon: 'ri-alarm-warning-line',
      message: 'Your health needs attention, but it\'s never too late to start making positive changes. We\'re here to support you every step of the way.',
      recommendations: [
        'Schedule a health checkup as soon as possible',
        'Reach out to our team for personalized guidance',
        'Start with small, achievable goals',
        'Consider our comprehensive wellness programs',
      ],
    };
  }
};

export default function HealthAssessmentQuiz() {
  const [isStarted, setIsStarted] = useState(false);
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [answers, setAnswers] = useState<number[]>([]);
  const [showResults, setShowResults] = useState(false);

  const handleAnswer = (score: number) => {
    const newAnswers = [...answers, score];
    setAnswers(newAnswers);

    if (currentQuestion < questions.length - 1) {
      setCurrentQuestion(currentQuestion + 1);
    } else {
      setShowResults(true);
    }
  };

  const resetQuiz = () => {
    setIsStarted(false);
    setCurrentQuestion(0);
    setAnswers([]);
    setShowResults(false);
  };

  const totalScore = answers.reduce((sum, score) => sum + score, 0);
  const result = getResultCategory(totalScore);
  const progress = ((currentQuestion + 1) / questions.length) * 100;

  // Calculate category scores
  const categoryScores = {
    'Heart Health': answers.slice(0, 2).reduce((a, b) => a + b, 0),
    'Nutrition': answers.slice(2, 4).reduce((a, b) => a + b, 0),
    'Mental Wellness': answers.slice(4, 6).reduce((a, b) => a + b, 0),
    'Sleep': answers.slice(6, 8).reduce((a, b) => a + b, 0),
    'Preventive Care': answers.slice(8, 10).reduce((a, b) => a + b, 0),
  };

  return (
    <section className="py-20 bg-gradient-to-br from-teal-50 via-cyan-50 to-white">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-12"
        >
          <div className="w-16 h-16 flex items-center justify-center bg-teal-500/10 rounded-2xl mx-auto mb-6">
            <i className="ri-questionnaire-line text-4xl text-teal-600"></i>
          </div>
          <h2 className="font-serif text-4xl lg:text-5xl font-bold text-charcoal mb-4">
            Health Assessment Quiz
          </h2>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto">
            Take our quick 10-question assessment to evaluate your overall health and wellness. Get personalized recommendations based on your results.
          </p>
        </motion.div>

        <AnimatePresence mode="wait">
          {!isStarted && !showResults && (
            <motion.div
              key="start"
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              className="bg-white rounded-3xl shadow-xl p-8 md:p-12 text-center"
            >
              <div className="w-24 h-24 flex items-center justify-center bg-gradient-to-br from-teal-400 to-cyan-500 rounded-full mx-auto mb-6">
                <i className="ri-heart-add-line text-5xl text-white"></i>
              </div>
              <h3 className="font-serif text-2xl font-bold text-charcoal mb-4">
                Ready to Check Your Health?
              </h3>
              <p className="text-gray-600 mb-8 max-w-lg mx-auto">
                This assessment covers 5 key areas: Heart Health, Nutrition, Mental Wellness, Sleep, and Preventive Care. It takes about 3 minutes to complete.
              </p>
              
              <div className="grid grid-cols-5 gap-4 mb-8">
                {[
                  { icon: 'ri-heart-pulse-line', label: 'Heart', color: 'red' },
                  { icon: 'ri-leaf-line', label: 'Nutrition', color: 'green' },
                  { icon: 'ri-mental-health-line', label: 'Mental', color: 'purple' },
                  { icon: 'ri-moon-line', label: 'Sleep', color: 'indigo' },
                  { icon: 'ri-stethoscope-line', label: 'Preventive', color: 'teal' },
                ].map((item, index) => (
                  <div key={index} className="text-center">
                    <div className={`w-12 h-12 flex items-center justify-center bg-${item.color}-100 rounded-xl mx-auto mb-2`}>
                      <i className={`${item.icon} text-xl text-${item.color}-500`}></i>
                    </div>
                    <span className="text-xs text-gray-600">{item.label}</span>
                  </div>
                ))}
              </div>

              <button
                onClick={() => setIsStarted(true)}
                className="bg-gradient-to-r from-teal-500 to-cyan-500 text-white px-10 py-4 rounded-full font-semibold hover:from-teal-600 hover:to-cyan-600 transition-all shadow-lg hover:shadow-xl cursor-pointer whitespace-nowrap"
              >
                Start Assessment
                <i className="ri-arrow-right-line ml-2"></i>
              </button>
            </motion.div>
          )}

          {isStarted && !showResults && (
            <motion.div
              key="quiz"
              initial={{ opacity: 0, x: 50 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -50 }}
              className="bg-white rounded-3xl shadow-xl overflow-hidden"
            >
              {/* Progress Bar */}
              <div className="bg-gray-100 h-2">
                <motion.div
                  initial={{ width: 0 }}
                  animate={{ width: `${progress}%` }}
                  className="h-full bg-gradient-to-r from-teal-500 to-cyan-500"
                />
              </div>

              <div className="p-8 md:p-12">
                {/* Question Header */}
                <div className="flex items-center justify-between mb-8">
                  <div className="flex items-center space-x-3">
                    <div className="w-10 h-10 flex items-center justify-center bg-teal-100 rounded-xl">
                      <i className={`${questions[currentQuestion].icon} text-xl text-teal-600`}></i>
                    </div>
                    <span className="text-sm font-semibold text-teal-600">
                      {questions[currentQuestion].category}
                    </span>
                  </div>
                  <span className="text-sm text-gray-500">
                    Question {currentQuestion + 1} of {questions.length}
                  </span>
                </div>

                {/* Question */}
                <h3 className="font-serif text-2xl font-bold text-charcoal mb-8">
                  {questions[currentQuestion].question}
                </h3>

                {/* Options */}
                <div className="space-y-4">
                  {questions[currentQuestion].options.map((option, index) => (
                    <motion.button
                      key={index}
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: index * 0.1 }}
                      onClick={() => handleAnswer(option.score)}
                      className="w-full text-left p-5 rounded-2xl border-2 border-gray-200 hover:border-teal-400 hover:bg-teal-50 transition-all cursor-pointer group"
                    >
                      <div className="flex items-center space-x-4">
                        <div className="w-8 h-8 flex items-center justify-center rounded-full border-2 border-gray-300 group-hover:border-teal-500 group-hover:bg-teal-500 transition-all">
                          <span className="text-sm font-semibold text-gray-500 group-hover:text-white">
                            {String.fromCharCode(65 + index)}
                          </span>
                        </div>
                        <span className="text-gray-700 group-hover:text-charcoal font-medium">
                          {option.text}
                        </span>
                      </div>
                    </motion.button>
                  ))}
                </div>
              </div>
            </motion.div>
          )}

          {showResults && (
            <motion.div
              key="results"
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              className="bg-white rounded-3xl shadow-xl overflow-hidden"
            >
              {/* Result Header */}
              <div className={`bg-gradient-to-r from-${result.color}-500 to-${result.color}-400 p-8 text-center text-white`}>
                <div className="w-20 h-20 flex items-center justify-center bg-white/20 rounded-full mx-auto mb-4">
                  <i className={`${result.icon} text-4xl`}></i>
                </div>
                <h3 className="font-serif text-3xl font-bold mb-2">{result.title}</h3>
                <p className="text-white/90 max-w-lg mx-auto">{result.message}</p>
              </div>

              <div className="p-8 md:p-12">
                {/* Score Overview */}
                <div className="text-center mb-10">
                  <div className="inline-flex items-center justify-center w-32 h-32 rounded-full bg-gradient-to-br from-gray-100 to-gray-50 mb-4">
                    <div className="text-center">
                      <span className={`text-4xl font-bold text-${result.color}-500`}>{totalScore}</span>
                      <span className="text-gray-400 text-lg">/40</span>
                    </div>
                  </div>
                  <p className="text-gray-600">Your Overall Health Score</p>
                </div>

                {/* Category Breakdown */}
                <div className="mb-10">
                  <h4 className="font-bold text-lg text-charcoal mb-6 text-center">Score by Category</h4>
                  <div className="grid md:grid-cols-5 gap-4">
                    {Object.entries(categoryScores).map(([category, score], index) => {
                      const maxScore = 8;
                      const percentage = (score / maxScore) * 100;
                      const colors = ['red', 'green', 'purple', 'indigo', 'teal'];
                      const icons = ['ri-heart-pulse-line', 'ri-leaf-line', 'ri-mental-health-line', 'ri-moon-line', 'ri-stethoscope-line'];
                      return (
                        <div key={category} className="text-center">
                          <div className={`w-14 h-14 flex items-center justify-center bg-${colors[index]}-100 rounded-xl mx-auto mb-3`}>
                            <i className={`${icons[index]} text-2xl text-${colors[index]}-500`}></i>
                          </div>
                          <div className="mb-2">
                            <span className={`text-xl font-bold text-${colors[index]}-500`}>{score}</span>
                            <span className="text-gray-400 text-sm">/8</span>
                          </div>
                          <p className="text-xs text-gray-600">{category}</p>
                          <div className="mt-2 h-2 bg-gray-100 rounded-full overflow-hidden">
                            <div 
                              className={`h-full bg-${colors[index]}-500 rounded-full`}
                              style={{ width: `${percentage}%` }}
                            />
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>

                {/* Recommendations */}
                <div className={`bg-${result.color}-50 rounded-2xl p-6 mb-8`}>
                  <h4 className="font-bold text-lg text-charcoal mb-4 flex items-center">
                    <i className="ri-lightbulb-flash-line text-amber-500 mr-2"></i>
                    Personalized Recommendations
                  </h4>
                  <ul className="space-y-3">
                    {result.recommendations.map((rec, index) => (
                      <li key={index} className="flex items-start space-x-3">
                        <i className={`ri-checkbox-circle-fill text-${result.color}-500 mt-0.5`}></i>
                        <span className="text-gray-700">{rec}</span>
                      </li>
                    ))}
                  </ul>
                </div>

                {/* Action Buttons */}
                <div className="flex flex-col sm:flex-row gap-4 justify-center">
                  <button
                    onClick={resetQuiz}
                    className="flex items-center justify-center space-x-2 bg-gray-100 text-charcoal px-8 py-4 rounded-full font-semibold hover:bg-gray-200 transition-all cursor-pointer whitespace-nowrap"
                  >
                    <i className="ri-restart-line"></i>
                    <span>Retake Quiz</span>
                  </button>
                  <a
                    href="/contact"
                    className="flex items-center justify-center space-x-2 bg-gradient-to-r from-coral to-pink text-white px-8 py-4 rounded-full font-semibold hover:from-coral/90 hover:to-pink/90 transition-all shadow-lg cursor-pointer whitespace-nowrap"
                  >
                    <span>Get Personalized Support</span>
                    <i className="ri-arrow-right-line"></i>
                  </a>
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Disclaimer */}
        <motion.p
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          className="text-center text-sm text-gray-500 mt-8"
        >
          <i className="ri-information-line mr-1"></i>
          This assessment is for educational purposes only and is not a substitute for professional medical advice. 
          Please consult with a healthcare provider for personalized health guidance.
        </motion.p>
      </div>
    </section>
  );
}
